import React, { useRef, useState } from "react";
import { ReactComponent as ImageFingerprint } from '../../public/images/svg/ImageFingerprint.svg';
import './css/GestureSwipe.css';

export interface GestureSwipeProps {
    onValueChange?: (event: { value: number }) => void;
    onChangeStart?: () => void;
    onChangeEnd?: (event: { value: number }) => void;
}

export const GestureSwipe: React.FC<GestureSwipeProps> = ({
    onValueChange,
    onChangeStart,
    onChangeEnd
}) => {

    const dynamicRef = useRef<HTMLDivElement | null>(null);
    const widthRef = useRef<number>(0);
    const startX = useRef<number | null>(null);
    const lastX = useRef<number | null>(null);
    const lastTime = useRef<number | null>(null);

    const [isDragging, setIsDragging] = useState(false);

    const fingerprintContent = (
        <ImageFingerprint width={38.85} height={48} viewBox="0 0 25.9 32" />
    );

    const setTranslateX = (x: number) => {
        dynamicRef.current?.style.setProperty("--gs-translate-x", `${x}px`);
    };

    const enableTransition = (enabled: boolean) => {
        dynamicRef.current?.style.setProperty("--gs-transition-enabled", enabled ? "1" : "0");
    };

    const resetPosition = () => {
        enableTransition(true);
        setTranslateX(0);
    };

    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        startX.current = e.clientX;
        lastX.current = e.clientX;
        lastTime.current = performance.now();
        setIsDragging(true);
        if (onChangeStart) {
            onChangeStart();
        }

        enableTransition(false);

        // measure width of the dynamic fingerprint container
        if (dynamicRef.current) {
            widthRef.current = dynamicRef.current.offsetWidth;
        }

        e.currentTarget.setPointerCapture(e.pointerId);
    };
    
    const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!isDragging || startX.current === null) return;

        const currentX = e.clientX;
        const deltaX = Math.max(0, currentX - startX.current);

        // normalised value (0–1)
        const width = widthRef.current || 1;
        const value = Math.min(1, deltaX / width);

        setTranslateX(deltaX);

        onValueChange?.({ value });
    };

    const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!isDragging) return;

        // Release capture from the same element that captured it
        e.currentTarget.releasePointerCapture(e.pointerId);

        setIsDragging(false);
        resetPosition();

        onChangeEnd?.({ value: 0 });

        startX.current = null;
        lastX.current = null;
        lastTime.current = null;
    };

    return (
        <div
            className="gesture-swipe-container"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}>
            <div className="fingerprint-container static">
                {fingerprintContent}
            </div>

            <div className="fingerprint-container dynamic" ref={dynamicRef}>
                {fingerprintContent}
            </div>
        </div>
    );
};

export default GestureSwipe;
